import React from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";

interface NotificationSummaryProps {
  templateName: string | null;
  notificationTitle?: string;
  targetUserCount: number;
  reachableUserCount?: number;
  audienceLoading?: boolean;
  sendMode: "immediate" | "scheduled" | "draft";
  scheduledFor: string;
  enableABTesting: boolean;
  abTestVariantCount: number;
  enableAnalytics: boolean;
  sending: boolean;
  sendProgress: { current: number; total: number; message: string } | null;
  canSend: boolean;
  campaignName?: string;
  onSendModeChange: (mode: "immediate" | "scheduled" | "draft") => void;
  onSend: () => void;
  onReset: () => void;
}

export default function NotificationSummary({
  templateName,
  notificationTitle,
  targetUserCount,
  reachableUserCount,
  audienceLoading,
  sendMode,
  scheduledFor,
  enableABTesting,
  abTestVariantCount,
  enableAnalytics,
  sending,
  sendProgress,
  canSend,
  campaignName,
  onSendModeChange,
  onSend,
  onReset,
}: NotificationSummaryProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="h-4 w-4" />
          Summary & Send
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary rows */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Template:</span>
            <span className="font-medium truncate ml-2">
              {templateName || "Direct message"}
            </span>
          </div>
          {notificationTitle && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Title:</span>
              <span className="font-medium truncate ml-2 max-w-[180px]">
                {notificationTitle}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Target Users:</span>
            <span className="font-medium">
              {audienceLoading ? (
                <Loader2 className="h-3 w-3 animate-spin inline" />
              ) : (
                `${targetUserCount} users`
              )}
            </span>
          </div>
          {reachableUserCount !== undefined && !audienceLoading && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Reachable:</span>
              <span className={`font-medium ${reachableUserCount < targetUserCount ? 'text-amber-600' : 'text-green-600'}`}>
                {reachableUserCount} with tokens
              </span>
            </div>
          )}
          {sendMode === "scheduled" && scheduledFor && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Scheduled:</span>
              <span className="font-medium">
                {new Date(scheduledFor).toLocaleString()}
              </span>
            </div>
          )}
          {sendMode === "draft" && campaignName && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Campaign:</span>
              <span className="font-medium truncate ml-2 max-w-[180px]">
                {campaignName}
              </span>
            </div>
          )}
          {enableABTesting && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">A/B Variants:</span>
              <span className="font-medium">{abTestVariantCount}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Analytics:</span>
            <span className="font-medium">
              {enableAnalytics ? "On" : "Off"}
            </span>
          </div>
        </div>

        <Separator />

        {/* Send Mode dropdown */}
        <div>
          <Label className="text-xs">Send Mode</Label>
          <Select
            value={sendMode}
            onValueChange={(value: "immediate" | "scheduled" | "draft") =>
              onSendModeChange(value)
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">Send Immediately</SelectItem>
              <SelectItem value="scheduled">Schedule for Later</SelectItem>
              <SelectItem value="draft">Save as Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Progress Display */}
        {sendProgress && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Sending...</span>
              <span className="text-muted-foreground">
                {sendProgress.current} / {sendProgress.total}
              </span>
            </div>
            <Progress
              value={(sendProgress.current / sendProgress.total) * 100}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              {sendProgress.message}
            </p>
          </div>
        )}

        {/* Action buttons — single row */}
        <div className="flex items-center gap-2">
          <Button
            onClick={onSend}
            disabled={sending || !canSend}
            className="flex-1"
            size="sm"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {sendMode === "immediate"
                  ? "Send Now"
                  : sendMode === "scheduled"
                    ? "Schedule"
                    : "Save Draft"}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onReset}
            disabled={sending}
            size="sm"
          >
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
